import React from 'react';

// Enums
export enum UserCategory {
  VISITOR = 'visitor',
  GUARDIAN = 'guardian',
  STAFF = 'staff',
  EXTERNAL = 'external',
  STUDENT = 'student',
}

export enum UserType {
  PARENTS = 'parents',
  VISITORS = 'visitors',
  GUESTS = 'guests',
  INTERNAL_USERS = 'internal_users',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  BLOCKED = 'blocked',
}

export enum VisitorStatus {
  SCHEDULED = 'scheduled',
  CHECKED_IN = 'checked_in',
  CHECKED_OUT = 'checked_out',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export enum ParentRelation {
  FATHER = 'father',
  MOTHER = 'mother',
  GUARDIAN = 'guardian',
  STEP_FATHER = 'step_father',
  STEP_MOTHER = 'step_mother',
  GRANDPARENT = 'grandparent',
  OTHER = 'other',
}

export enum GuestType {
  VENDOR = 'vendor',
  CONTRACTOR = 'contractor',
  SPEAKER = 'speaker',
  VOLUNTEER = 'volunteer',
  OFFICIAL = 'official',
  OTHER = 'other',
}

// Base User Interface
export interface BaseUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profilePicture?: string;
  status: UserStatus;
  createdDate: string;
  lastActive: string;
  notes?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

// Parent/Guardian Interface
export interface ParentUser extends BaseUser {
  relation: ParentRelation;
  occupation?: string;
  workPhone?: string;
  emergencyContact: boolean;
  children: {
    studentId: string;
    studentName: string;
    grade: string;
    section: string;
  }[];
  meetingHistory: {
    id: string;
    date: string;
    type: 'parent_teacher' | 'disciplinary' | 'academic' | 'general';
    teacher: string;
    subject: string;
    notes: string;
    status: 'scheduled' | 'completed' | 'cancelled';
  }[];
  communicationPreferences: {
    email: boolean;
    sms: boolean;
    phone: boolean;
    push: boolean;
  };
}

// Visitor Interface
export interface VisitorUser extends BaseUser {
  visitDate: string;
  visitTime: string;
  visitDuration?: number; // in minutes
  visitPurpose: string;
  hostPerson: string;
  hostDepartment: string;
  visitStatus: VisitorStatus;
  checkInTime?: string;
  checkOutTime?: string;
  idDocumentType: string;
  idDocumentNumber: string;
  visitorBadgeNumber?: string;
  securityClearance: 'basic' | 'standard' | 'high';
  vehicleDetails?: {
    make: string;
    model: string;
    plateNumber: string;
    parkingSpot?: string;
  };
  visitHistory: {
    id: string;
    date: string;
    purpose: string;
    duration: number;
    host: string;
    status: VisitorStatus;
  }[];
}

// Guest Interface
export interface GuestUser extends BaseUser {
  guestType: GuestType;
  organization?: string;
  designation?: string;
  eventId?: string;
  eventName?: string;
  invitedBy: string;
  invitationDate: string;
  accessLevel: 'limited' | 'standard' | 'full';
  accommodation?: {
    required?: boolean;
    roomNumber?: string;
    roomType?: string;
    building?: string;
    checkIn?: string;
    checkOut?: string;
    specialRequests?: string;
  };
  services: {
    id: string;
    serviceName: string;
    description: string;
    startDate: string;
    endDate: string;
    cost?: number;
    status: 'pending' | 'active' | 'completed' | 'cancelled';
  }[];
  documents: {
    id: string;
    type: string;
    name: string;
    uploadDate: string;
    verified: boolean;
  }[];
}

// Summary Statistics
export interface UsersSummary {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  blockedUsers: number;
  recentlyAdded: number;
  statusDistribution: {
    active: number;
    inactive: number;
    pending: number;
    blocked: number;
  };
}

// Filter Interfaces
export interface UsersFilters {
  userType: UserType;
  status: UserStatus | 'all';
  searchQuery: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  // Type-specific filters
  relation?: ParentRelation | 'all';
  visitStatus?: VisitorStatus | 'all';
  guestType?: GuestType | 'all';
}

// Pagination Params
export interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

// Context State Interface
export interface UsersState {
  // Current view type
  currentUserType: UserType;
  currentCategory: UserCategory;
  activeTabIndex: number; // Track which tab is currently active

  // Data
  parentUsers: import('../../../apis/users/users.api.interfaces').ParentUser[];
  visitorUsers: import('../../../apis/users/users.api.interfaces').VisitorUser[];
  guestUsers: import('../../../apis/users/users.api.interfaces').GuestUser[];
  internalUsers: import('../../../apis/users/users.api.interfaces').InternalUser[];

  // Pagination states
  parentPagination: PaginationState;
  visitorPagination: PaginationState;
  guestPagination: PaginationState;
  internalPagination: PaginationState;

  // Summary statistics
  parentsSummary: import('../../../apis/users/users.api.interfaces').UsersSummary;
  visitorsSummary: import('../../../apis/users/users.api.interfaces').VisitorsSummary;
  guestsSummary: import('../../../apis/users/users.api.interfaces').UsersSummary;
  internalUsersSummary: import('../../../apis/users/users.api.interfaces').UsersSummary;

  // UI State
  filters: UsersFilters;
  isLoading: boolean;
  error: string | null;
  selectedUser: import('../../../apis/users/users.api.interfaces').BaseUser | null;

  // Add/Edit State
  isAddEditParentOpen: boolean;
  isAddEditVisitorOpen: boolean;
  isAddEditGuestOpen: boolean;
  isAddEditInternalOpen: boolean;
  isCreateMode: boolean;
  isEditMode: boolean;
}

// Action Types
export enum UsersActionType {
  SET_USER_TYPE = 'SET_USER_TYPE',
  SET_ACTIVE_TAB = 'SET_ACTIVE_TAB',
  SET_CATEGORY = 'SET_CATEGORY',
  SET_PARENT_USERS = 'SET_PARENT_USERS',
  SET_VISITOR_USERS = 'SET_VISITOR_USERS',
  SET_GUEST_USERS = 'SET_GUEST_USERS',
  SET_FILTERS = 'SET_FILTERS',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_SELECTED_USER = 'SET_SELECTED_USER',
  UPDATE_PARENTS_SUMMARY = 'UPDATE_PARENTS_SUMMARY',
  UPDATE_VISITORS_SUMMARY = 'UPDATE_VISITORS_SUMMARY',
  UPDATE_GUESTS_SUMMARY = 'UPDATE_GUESTS_SUMMARY',
  SET_PARENT_PAGINATION = 'SET_PARENT_PAGINATION',
  SET_VISITOR_PAGINATION = 'SET_VISITOR_PAGINATION',
  SET_GUEST_PAGINATION = 'SET_GUEST_PAGINATION',
  RESET_FILTERS = 'RESET_FILTERS',
  SET_IS_ADD_EDIT_PARENT_OPEN = 'SET_IS_ADD_EDIT_PARENT_OPEN',
  SET_IS_ADD_EDIT_VISITOR_OPEN = 'SET_IS_ADD_EDIT_VISITOR_OPEN',
  SET_IS_ADD_EDIT_GUEST_OPEN = 'SET_IS_ADD_EDIT_GUEST_OPEN',
  SET_IS_ADD_EDIT_INTERNAL_OPEN = 'SET_IS_ADD_EDIT_INTERNAL_OPEN',
  SET_INTERNAL_USERS = 'SET_INTERNAL_USERS',
  SET_INTERNAL_PAGINATION = 'SET_INTERNAL_PAGINATION',
  UPDATE_INTERNAL_USERS_SUMMARY = 'UPDATE_INTERNAL_USERS_SUMMARY',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
}

// Action Interfaces
export interface SetUserTypeAction {
  type: UsersActionType.SET_USER_TYPE;
  payload: UserType;
}

export interface SetActiveTabAction {
  type: UsersActionType.SET_ACTIVE_TAB;
  payload: number;
}

export interface SetCategoryAction {
  type: UsersActionType.SET_CATEGORY;
  payload: UserCategory;
}

export interface SetParentPaginationAction {
  type: UsersActionType.SET_PARENT_PAGINATION;
  payload: Partial<PaginationState>;
}

export interface SetVisitorPaginationAction {
  type: UsersActionType.SET_VISITOR_PAGINATION;
  payload: Partial<PaginationState>;
}

export interface SetGuestPaginationAction {
  type: UsersActionType.SET_GUEST_PAGINATION;
  payload: Partial<PaginationState>;
}

export interface SetParentUsersAction {
  type: UsersActionType.SET_PARENT_USERS;
  payload: import('../../../apis/users/users.api.interfaces').ParentUser[];
}

export interface SetVisitorUsersAction {
  type: UsersActionType.SET_VISITOR_USERS;
  payload: import('../../../apis/users/users.api.interfaces').VisitorUser[];
}

export interface SetGuestUsersAction {
  type: UsersActionType.SET_GUEST_USERS;
  payload: import('../../../apis/users/users.api.interfaces').GuestUser[];
}

export interface SetFiltersAction {
  type: UsersActionType.SET_FILTERS;
  payload: Partial<UsersFilters>;
}

export interface SetSearchQueryAction {
  type: UsersActionType.SET_SEARCH_QUERY;
  payload: string;
}

export interface SetLoadingAction {
  type: UsersActionType.SET_LOADING;
  payload: boolean;
}

export interface SetErrorAction {
  type: UsersActionType.SET_ERROR;
  payload: string | null;
}

export interface SetSelectedUserAction {
  type: UsersActionType.SET_SELECTED_USER;
  payload: import('../../../apis/users/users.api.interfaces').BaseUser | null;
}

export interface UpdateParentsSummaryAction {
  type: UsersActionType.UPDATE_PARENTS_SUMMARY;
  payload: import('../../../apis/users/users.api.interfaces').UsersSummary;
}

export interface UpdateVisitorsSummaryAction {
  type: UsersActionType.UPDATE_VISITORS_SUMMARY;
  payload: import('../../../apis/users/users.api.interfaces').VisitorsSummary;
}

export interface UpdateGuestsSummaryAction {
  type: UsersActionType.UPDATE_GUESTS_SUMMARY;
  payload: import('../../../apis/users/users.api.interfaces').UsersSummary;
}

export interface ResetFiltersAction {
  type: UsersActionType.RESET_FILTERS;
}

export interface SetIsAddEditParentOpenAction {
  type: UsersActionType.SET_IS_ADD_EDIT_PARENT_OPEN;
  payload: boolean;
}

export interface SetIsAddEditVisitorOpenAction {
  type: UsersActionType.SET_IS_ADD_EDIT_VISITOR_OPEN;
  payload: boolean;
}

export interface SetIsAddEditGuestOpenAction {
  type: UsersActionType.SET_IS_ADD_EDIT_GUEST_OPEN;
  payload: boolean;
}

export interface SetIsAddEditInternalOpenAction {
  type: UsersActionType.SET_IS_ADD_EDIT_INTERNAL_OPEN;
  payload: boolean;
}

export interface SetInternalUsersAction {
  type: UsersActionType.SET_INTERNAL_USERS;
  payload: import('../../../apis/users/users.api.interfaces').InternalUser[];
}

export interface SetInternalPaginationAction {
  type: UsersActionType.SET_INTERNAL_PAGINATION;
  payload: Partial<PaginationState>;
}

export interface UpdateInternalUsersSummaryAction {
  type: UsersActionType.UPDATE_INTERNAL_USERS_SUMMARY;
  payload: import('../../../apis/users/users.api.interfaces').UsersSummary;
}

export interface SetIsCreateModeAction {
  type: UsersActionType.SET_IS_CREATE_MODE;
  payload: boolean;
}

export interface SetIsEditModeAction {
  type: UsersActionType.SET_IS_EDIT_MODE;
  payload: boolean;
}

export type UsersAction =
  | SetUserTypeAction
  | SetActiveTabAction
  | SetCategoryAction
  | SetParentUsersAction
  | SetVisitorUsersAction
  | SetGuestUsersAction
  | SetFiltersAction
  | SetSearchQueryAction
  | SetLoadingAction
  | SetErrorAction
  | SetSelectedUserAction
  | UpdateParentsSummaryAction
  | UpdateVisitorsSummaryAction
  | UpdateGuestsSummaryAction
  | SetParentPaginationAction
  | SetVisitorPaginationAction
  | SetGuestPaginationAction
  | ResetFiltersAction
  | SetIsAddEditParentOpenAction
  | SetIsAddEditVisitorOpenAction
  | SetIsAddEditGuestOpenAction
  | SetIsAddEditInternalOpenAction
  | SetInternalUsersAction
  | SetInternalPaginationAction
  | UpdateInternalUsersSummaryAction
  | SetIsCreateModeAction
  | SetIsEditModeAction;

// Context Interface
export interface UsersContextType {
  state: UsersState;
  dispatch: React.Dispatch<UsersAction>;

  // Action Creators
  setUserType: (type: UserType) => void;
  setActiveTab: (index: number) => void;
  setFilters: (filters: Partial<UsersFilters>) => void;
  setSearchQuery: (query: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedUser: (user: import('../../../apis/users/users.api.interfaces').BaseUser | null) => void;
  setParentPage: (page: number) => void;
  setVisitorPage: (page: number) => void;
  setGuestPage: (page: number) => void;
  setInternalPage: (page: number) => void;
  resetFilters: () => void;

  // Data fetching methods
  fetchParentUsers: () => Promise<void>;
  fetchVisitorUsers: () => Promise<void>;
  fetchGuestUsers: () => Promise<void>;

  // Utility methods
  getFilteredUsers: () => import('../../../apis/users/users.api.interfaces').BaseUser[];
  getFilteredParentUsers: () => import('../../../apis/users/users.api.interfaces').ParentUser[];
  getFilteredVisitorUsers: () => import('../../../apis/users/users.api.interfaces').VisitorUser[];
  getFilteredGuestUsers: () => import('../../../apis/users/users.api.interfaces').GuestUser[];
  getCurrentSummary: () => import('../../../apis/users/users.api.interfaces').UsersSummary | import('../../../apis/users/users.api.interfaces').VisitorsSummary;
  exportUsers: (format: 'pdf' | 'excel') => Promise<void>;

  // Add/Edit methods
  openAddParent: () => void;
  openEditParent: (user: import('../../../apis/users/users.api.interfaces').ParentUser) => void;
  closeAddEditParent: () => void;
  openAddVisitor: () => void;
  openEditVisitor: (user: import('../../../apis/users/users.api.interfaces').VisitorUser) => void;
  closeAddEditVisitor: () => void;
  openAddGuest: () => void;
  openEditGuest: (user: import('../../../apis/users/users.api.interfaces').GuestUser) => void;
  closeAddEditGuest: () => void;
  openAddInternal: () => void;
  closeAddEditInternal: () => void;
}

// Component Props Interfaces
export interface UsersViewProps {
  // Any props needed for the main view
}

export interface ParentUsersViewProps {
  users: import('../../../apis/users/users.api.interfaces').ParentUser[];
  summary: import('../../../apis/users/users.api.interfaces').UsersSummary | import('../../../apis/users/users.api.interfaces').VisitorsSummary;
  onUserSelect: (user: import('../../../apis/users/users.api.interfaces').ParentUser) => void;
}

export interface VisitorUsersViewProps {
  users: import('../../../apis/users/users.api.interfaces').VisitorUser[];
  summary: import('../../../apis/users/users.api.interfaces').UsersSummary | import('../../../apis/users/users.api.interfaces').VisitorsSummary;
  onUserSelect: (user: import('../../../apis/users/users.api.interfaces').VisitorUser) => void;
}

export interface GuestUsersViewProps {
  users: import('../../../apis/users/users.api.interfaces').GuestUser[];
  summary: import('../../../apis/users/users.api.interfaces').UsersSummary | import('../../../apis/users/users.api.interfaces').VisitorsSummary;
  onUserSelect: (user: import('../../../apis/users/users.api.interfaces').GuestUser) => void;
}
